'use client';

import { useCallback, useRef, useState } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

export function StaffAvailabilityFilter({
  staff,
  selectedStaffId,
  onSelectStaff,
  isLoading,
}: {
  staff: { id: string; name: string }[];
  selectedStaffId: string;
  onSelectStaff: (staffId: string) => void;
  isLoading: boolean;
}) {
  const { t, i18n } = useTranslation('bookings');
  const isRTL = i18n.dir() === 'rtl';
  const scrollRef = useRef<HTMLDivElement>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const [scrollState, setScrollState] = useState({
    hasOverflow: false,
    canScrollBack: false,
    canScrollForward: false,
  });

  // Observe the viewport and content so arrows follow resizing, fonts and touch scrolling.
  const observeScroll = useCallback((track: HTMLDivElement | null) => {
    if (!track) return;
    const row = track.parentElement;
    const content = track.firstElementChild;
    if (!row || !content) return;

    scrollRef.current = track;
    let revealSelection = true;

    const update = () => {
      if (track.clientWidth === 0) return;

      if (revealSelection) {
        revealSelection = false;
        const selected = track.querySelector('[aria-pressed="true"]');
        if (selected) {
          const selectedBounds = selected.getBoundingClientRect();
          const trackBounds = track.getBoundingClientRect();
          if (
            selectedBounds.left < trackBounds.left ||
            selectedBounds.right > trackBounds.right
          ) {
            track.scrollBy({
              left:
                selectedBounds.left +
                selectedBounds.width / 2 -
                trackBounds.left -
                trackBounds.width / 2,
              behavior: 'instant',
            });
          }
        }
      }

      const offset = Math.abs(track.scrollLeft);
      const next = {
        hasOverflow: content.scrollWidth > row.clientWidth + 1,
        canScrollBack: offset > 1,
        canScrollForward: offset < track.scrollWidth - track.clientWidth - 1,
      };
      if (
        !next.canScrollBack &&
        document.activeElement === backButtonRef.current
      ) {
        content.querySelector<HTMLButtonElement>('button')?.focus({
          preventScroll: true,
        });
      }
      setScrollState((current) =>
        current.hasOverflow === next.hasOverflow &&
        current.canScrollBack === next.canScrollBack &&
        current.canScrollForward === next.canScrollForward
          ? current
          : next,
      );
    };

    const observer = new ResizeObserver(update);
    [row, track, content].forEach((element) => observer.observe(element));
    track.addEventListener('scroll', update, { passive: true });

    return () => {
      observer.disconnect();
      track.removeEventListener('scroll', update);
      scrollRef.current = null;
    };
  }, []);

  const scroll = (forward: boolean) => {
    const track = scrollRef.current;
    if (!track) return;
    track.scrollBy({
      left: track.clientWidth * 0.8 * (forward !== isRTL ? 1 : -1),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'instant'
        : 'smooth',
    });
  };

  if (isLoading || staff.length === 0) return null;

  if (staff.length === 1) {
    return (
      <p
        className="text-muted-foreground mb-4 text-sm"
        data-test="single-available-staff"
      >
        <Trans
          i18nKey="bookings:staffFilter.withStaff"
          values={{ name: staff[0]!.name }}
        />
      </p>
    );
  }

  return (
    <fieldset className="mb-4 min-w-0" data-test="staff-availability-filter">
      <legend className="sr-only">
        <Trans i18nKey="bookings:staffFilter.label" />
      </legend>
      <div className="flex min-w-0 items-center gap-1" dir={isRTL ? 'rtl' : 'ltr'}>
        {scrollState.hasOverflow && scrollState.canScrollBack && (
          <Button
            ref={backButtonRef}
            type="button"
            variant="ghost"
            size="icon"
            className="size-11 shrink-0"
            aria-label={t('staffFilter.previousStaff')}
            data-test="staff-filter-scroll-back"
            onClick={() => scroll(false)}
          >
            {isRTL ? (
              <ChevronRight className="size-4" />
            ) : (
              <ChevronLeft className="size-4" />
            )}
          </Button>
        )}
        <div
          ref={observeScroll}
          className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-test="staff-filter-scroll-track"
        >
          <div className="flex w-max gap-1.5 p-1">
            {[{ id: '', name: t('staffFilter.anyStaff') }, ...staff].map(
              (person) => (
                <Button
                  key={person.id}
                  type="button"
                  variant={selectedStaffId === person.id ? 'default' : 'outline'}
                  aria-pressed={selectedStaffId === person.id}
                  data-native-booking-choice
                  data-test="staff-filter-option"
                  data-staff-id={person.id}
                  className="h-11 shrink-0 rounded-full px-3 py-2 text-xs whitespace-nowrap"
                  onClick={() => onSelectStaff(person.id)}
                >
                  {person.name}
                </Button>
              ),
            )}
          </div>
        </div>
        {scrollState.hasOverflow && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-11 shrink-0"
            disabled={!scrollState.canScrollForward}
            aria-label={t('staffFilter.nextStaff')}
            data-test="staff-filter-scroll-forward"
            onClick={() => scroll(true)}
          >
            {isRTL ? (
              <ChevronLeft className="size-4" />
            ) : (
              <ChevronRight className="size-4" />
            )}
          </Button>
        )}
      </div>
    </fieldset>
  );
}
